"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useAuth, isPasskeyCancel, passkeyNeedsClick, type PasskeyLoginStart, type SecondFactor } from "@/lib/auth-context";
import { useToast } from "@/components/toast-provider";
import { Spinner } from "@/lib/icons";
import * as s from "@/lib/styles";
import { ClearableInput } from "@/components/clearable-input";
import { resolvePostLoginPath } from "@/lib/post-login";

/** Sign-in is two steps for anyone with a second factor armed: password, then
 *  either a passkey (Touch ID / Face ID) or a 6-digit code. `login()` says which
 *  — "second-factor" means the password was accepted but no session exists yet.
 *  Users with no second factor never see the second step. */
export default function LoginPage() {
  const { user, loading, login, verifyOtp, beginPasskeyLogin, loginWithPasskey, needsSetup } = useAuth();
  const { showToast } = useToast();
  const router = useRouter();

  const [step, setStep] = useState<"password" | "passkey" | "otp">("password");
  const [methods, setMethods] = useState<SecondFactor[]>([]);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Set once the device prompt has been dismissed or has failed, so we can offer
  // it again instead of leaving the user staring at a spinner.
  const [passkeyStalled, setPasskeyStalled] = useState(false);
  const [passkeyReady, setPasskeyReady] = useState(false);
  const codeRef = useRef<HTMLInputElement | null>(null);
  const pendingPasskey = useRef<PasskeyLoginStart | null>(null);
  const needsClick = passkeyNeedsClick();

  const afterAuth = async (flags: { mustChangePassword: boolean; mustSetup2fa: boolean }) => {
    const needs = flags.mustChangePassword || flags.mustSetup2fa;
    router.push(await resolvePostLoginPath(needs));
  };

  useEffect(() => {
    if (loading || !user) return;
    let cancelled = false;
    void (async () => {
      const dest = await resolvePostLoginPath(needsSetup);
      if (!cancelled) router.replace(dest);
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, user, needsSetup, router]);

  if (loading || user) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-100px)]">
        <p className="text-muted">Loading...</p>
      </div>
    );
  }

  // Mac/iPhone: prompt immediately. Windows/Linux: wait for Continue so the
  // phone QR is allowed to appear.
  //
  // A 401 here usually means the *password* was wrong: with 2FA armed we never
  // say so at step one (so SSH passwords can't be probed), and the passkey can
  // still succeed. Send them back to re-type the password rather than blaming
  // the passkey.
  const runPasskey = async () => {
    setPasskeyStalled(false);
    setSubmitting(true);
    try {
      let start = pendingPasskey.current;
      if (!start) {
        start = await beginPasskeyLogin();
        pendingPasskey.current = start;
        setPasskeyReady(true);
      }
      const flags = await loginWithPasskey(start);
      pendingPasskey.current = null;
      await afterAuth(flags);
    } catch (err) {
      if (isPasskeyCancel(err)) {
        setPasskeyStalled(true);
      } else {
        pendingPasskey.current = null;
        const msg = err instanceof Error ? err.message : "Passkey sign-in failed";
        showToast("error", msg);
        setStep("password");
        setPassword("");
        setPasskeyStalled(false);
        setPasskeyReady(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePassword = async () => {
    setSubmitting(true);
    try {
      const res = await login(username, password);
      if (res.result === "second-factor") {
        setMethods(res.methods);
        setCode("");
        if (res.methods.includes("passkey")) {
          setStep("passkey");
          setPasskeyStalled(false);
          setPasskeyReady(false);
          pendingPasskey.current = null;
          try {
            pendingPasskey.current = await beginPasskeyLogin();
            setPasskeyReady(true);
          } catch {
            setPasskeyReady(false);
            setPasskeyStalled(true);
          }
          if (!needsClick) {
            await runPasskey();
          }
        } else {
          setStep("otp");
        }
        return;
      }
      await afterAuth(res);
    } catch (err) {
      showToast("error", err instanceof Error ? err.message : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Shared by the Verify button and the auto-submit on the 6th digit, so guard
  // against being entered twice for the same code.
  const submitCode = async (value: string) => {
    if (value.length !== 6 || submitting) return;
    setSubmitting(true);
    try {
      const flags = await verifyOtp(value);
      await afterAuth(flags);
    } catch (err) {
      showToast("error", err instanceof Error ? err.message : "Verification failed");
      setCode("");
      codeRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  const onCodeChange = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    // People expect a 6-digit code to submit itself; the button stays for a11y.
    if (digits.length === 6) void submitCode(digits);
  };

  const backToPassword = () => {
    setStep("password");
    setCode("");
    setPassword("");
    setPasskeyStalled(false);
    setPasskeyReady(false);
    pendingPasskey.current = null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step === "password") await handlePassword();
    else if (step === "passkey") await runPasskey();
    else await submitCode(code);
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-100px)]">
      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-6">
        {step === "password" && (
          <>
            <h1 className="text-2xl font-bold text-cyan text-center">Sign In</h1>
            <div className={s.card}>
              <div className={s.fieldRow}>
                <label className={s.label}>Username</label>
                <ClearableInput
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={s.input}
                  autoFocus
                  autoComplete="username"
                />
              </div>
              <div className={s.fieldRow}>
                <label className={s.label}>Password</label>
                <ClearableInput
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={s.input}
                  autoComplete="current-password"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Link
                href="/forgot-password"
                className="text-sm text-muted hover:text-foreground transition-colors"
              >
                Forgot password?
              </Link>
              <button type="submit" disabled={submitting} className={s.btnPrimary}>
                {submitting ? "Signing in..." : "Sign In"}
              </button>
            </div>
          </>
        )}

        {step === "passkey" && (
          <>
            <h1 className="text-2xl font-bold text-cyan text-center">Use Your Passkey</h1>
            <div className={s.card}>
              {passkeyStalled ? (
                <p className="text-sm text-muted">
                  Passkey was cancelled. Try again, or go Back and re-check your
                  password — a wrong password also fails at this step.
                </p>
              ) : submitting ? (
                <p className="flex items-center text-sm text-muted">
                  <Spinner className={s.statusIcon} />
                  Waiting for your passkey…
                </p>
              ) : needsClick ? (
                <p className="text-sm text-muted">
                  Press Continue. A QR code appears — scan it with the phone that
                  holds your passkey. Bluetooth must be on, on the phone and on
                  this computer.
                </p>
              ) : (
                <p className="flex items-center text-sm text-muted">
                  <Spinner className={s.statusIcon} />
                  Waiting for your passkey…
                </p>
              )}
            </div>
            <div className="flex items-center justify-end gap-4">
              <button
                type="button"
                onClick={backToPassword}
                className="text-sm text-muted hover:text-foreground transition-colors"
              >
                Back
              </button>
              {methods.includes("totp") && (
                <button
                  type="button"
                  onClick={() => { setStep("otp"); setCode(""); }}
                  className="text-sm text-cyan hover:text-foreground transition-colors"
                >
                  Use a code instead
                </button>
              )}
              <button
                type="submit"
                disabled={submitting || (needsClick && !passkeyReady && !passkeyStalled)}
                className={s.btnPrimary}
              >
                {submitting
                  ? "Waiting..."
                  : needsClick
                    ? passkeyReady
                      ? "Continue"
                      : "Preparing..."
                    : "Try Again"}
              </button>
            </div>
          </>
        )}

        {step === "otp" && (
          <>
            <h1 className="text-2xl font-bold text-cyan text-center">Two-Factor Authentication</h1>
            <div className={s.card}>
              <p className="text-sm text-muted">
                Enter the 6-digit code from your authenticator app.
              </p>
              <div className={s.fieldRow}>
                <label className={s.label}>Code</label>
                <ClearableInput
                  ref={codeRef}
                  type="text"
                  value={code}
                  onChange={(e) => onCodeChange(e.target.value)}
                  className={`${s.input} font-mono tracking-[0.3em]`}
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="000000"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-4">
              <button
                type="button"
                onClick={backToPassword}
                className="text-sm text-muted hover:text-foreground transition-colors"
              >
                Back
              </button>
              {methods.includes("passkey") && (
                <button
                  type="button"
                  onClick={() => { setStep("passkey"); void runPasskey(); }}
                  className="text-sm text-cyan hover:text-foreground transition-colors"
                >
                  Use your passkey
                </button>
              )}
              <button
                type="submit"
                disabled={submitting || code.length !== 6}
                className={s.btnPrimary}
              >
                {submitting ? "Verifying..." : "Verify"}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
